import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  X,
  Loader2,
  MessageCircle,
  ArrowUp,
  Sparkles,
  Calendar,
  TrendingUp,
  Trophy,
  Crown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import TierBadge from '@/components/TierBadge';
import { SentimentPill } from '@/components/CommunityPostBits';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = 'dividrip:welcomeBackSeen';

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

function timeAgo(iso) {
  if (!iso) return '';
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch {
    return '';
  }
}

function SectionRow({ icon: Icon, iconCls, label, value, sub }) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-border last:border-0">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${iconCls}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold">{label}</p>
        {sub ? <p className="text-xs text-muted-foreground mt-0.5">{sub}</p> : null}
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-base font-bold tabular-nums">{value}</p>
      </div>
    </div>
  );
}

function RepliesPreview({ replies }) {
  if (!replies || replies.length === 0) return null;
  return (
    <div className="mt-2 space-y-1.5">
      {replies.slice(0, 3).map((r) => (
        <div key={r.comment_id} className="rounded-lg bg-muted/30 px-3 py-2 text-xs">
          <p className="font-semibold text-foreground">
            @{r.handle} <span className="text-muted-foreground font-normal">· {timeAgo(r.created_at)}</span>
          </p>
          <p className="text-muted-foreground line-clamp-2 mt-0.5">{r.body}</p>
        </div>
      ))}
    </div>
  );
}

function ExDivList({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-2 grid sm:grid-cols-2 gap-1.5">
      {items.slice(0, 6).map((d) => (
        <div key={`${d.ticker}-${d.ex_dividend_date}`} className="flex items-center justify-between rounded-lg bg-muted/30 px-3 py-1.5 text-xs">
          <span className="font-mono font-bold text-violet-700 dark:text-violet-300">{d.ticker}</span>
          <span className="text-muted-foreground">{fmtDate(d.ex_dividend_date)}</span>
        </div>
      ))}
    </div>
  );
}

function SentimentList({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-2 space-y-1.5">
      {items.slice(0, 6).map((s) => (
        <div key={s.ticker} className="flex items-center justify-between gap-2 rounded-lg bg-muted/30 px-3 py-1.5 text-xs">
          <span className="font-mono font-bold text-violet-700 dark:text-violet-300">{s.ticker}</span>
          <div className="flex items-center gap-2">
            <SentimentPill label={s.label} />
            <span className="text-muted-foreground tabular-nums">
              {s.bullish_pct}% / {s.bearish_pct}%
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function PodiumList({ podium }) {
  if (!podium || !podium.top3 || podium.top3.length === 0) return null;
  const emojis = ['🥇', '🥈', '🥉'];
  return (
    <div className="mt-2 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3">
      <p className="text-xs font-bold text-amber-700 dark:text-amber-400 mb-1.5">
        Last week's podium · {podium.theme_name}
      </p>
      <div className="space-y-1">
        {podium.top3.map((p, i) => (
          <div key={`${p.handle}-${i}`} className="flex items-center justify-between text-xs">
            <span>
              {emojis[i] || `#${i + 1}`} <span className="font-semibold">@{p.handle}</span>
              {p.watchlist_name ? <span className="text-muted-foreground"> · {p.watchlist_name}</span> : null}
            </span>
            <span className="font-bold tabular-nums">{p.score_display}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function WelcomeBackModal() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const close = useCallback(async () => {
    setOpen(false);
    // Mark visit on server (best-effort — no toast on failure)
    try {
      await axios.post(`${API}/community/whats-new/seen`, {}, { withCredentials: true });
    } catch {
      /* ignore */
    }
    // Suppress re-show in this browser session
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    // Only show once per browser session even if user navigates away
    // and back to /community within the same tab.
    let seenThisSession = false;
    try {
      seenThisSession = sessionStorage.getItem(SESSION_KEY) === '1';
    } catch {
      /* ignore */
    }
    if (seenThisSession) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    axios
      .get(`${API}/community/whats-new`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
        if (res.data && res.data.has_anything) {
          setOpen(true);
        }
      })
      .catch(() => { /* not a member or auth issue — silently skip */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // ESC to close
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  if (loading || !open || !data) return null;

  const handleBackdrop = (e) => {
    // Click on backdrop (not inner card) — close AND stop propagation so
    // the click never reaches the underlying page.
    e.stopPropagation();
    e.preventDefault();
    close();
  };
  const stopBubble = (e) => { e.stopPropagation(); };

  const hasReplies = (data.replies?.count || 0) > 0;
  const hasUpvotes = (data.upvotes?.total || 0) > 0;
  const hasBuzz = (data.portfolio_buzz?.count || 0) > 0;
  const hasExDiv = (data.upcoming_ex_divs?.length || 0) > 0;
  const hasSent = (data.sentiment_held?.length || 0) > 0;
  const hasRank = !!data.my_leaderboard;
  const hasPodium = !!data.last_friday_podium;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
      onMouseDown={handleBackdrop}
      data-testid="welcome-back-backdrop"
    >
      <div
        className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto pb-24 sm:pb-4"
        onMouseDown={stopBubble}
        onClick={stopBubble}
        data-testid="welcome-back-modal"
      >
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white px-5 py-4 flex items-start gap-3 z-10">
          <Sparkles className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h2 className="text-base sm:text-lg font-bold leading-snug">
              Welcome back, here's what you missed while you were away.
            </h2>
            <p className="text-xs opacity-90 mt-1">
              Since {fmtDate(data.since_iso)} · @{data.handle}
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            className="p-1 rounded-md hover:bg-white/20 transition flex-shrink-0"
            data-testid="welcome-back-close-x"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-5 space-y-2">
          {/* Tier strip */}
          <div className="flex items-center justify-between rounded-lg bg-muted/30 px-3 py-2 mb-1">
            <div className="flex items-center gap-2">
              <Crown className="w-4 h-4 text-violet-500" />
              <span className="text-xs text-muted-foreground">Your tier</span>
              <TierBadge tier={data.tier || 'novice'} />
            </div>
            <span className="text-xs">
              <span className="font-bold tabular-nums">{data.points}</span>
              <span className="text-muted-foreground"> pts</span>
            </span>
          </div>

          {hasReplies ? (
            <div>
              <SectionRow
                icon={MessageCircle}
                iconCls="bg-blue-100 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400"
                label="New replies to your posts & comments"
                sub={`Last reply ${data.replies.preview[0]?.created_at ? timeAgo(data.replies.preview[0].created_at) : 'recently'}`}
                value={data.replies.count}
              />
              <RepliesPreview replies={data.replies.preview} />
            </div>
          ) : null}

          {hasUpvotes ? (
            <SectionRow
              icon={ArrowUp}
              iconCls="bg-orange-100 dark:bg-orange-950/40 text-orange-600 dark:text-orange-400"
              label="Upvotes received"
              sub={`${data.upvotes.posts} on posts · ${data.upvotes.comments} on comments`}
              value={data.upvotes.total}
            />
          ) : null}

          {hasBuzz ? (
            <div>
              <SectionRow
                icon={Sparkles}
                iconCls="bg-violet-100 dark:bg-violet-950/40 text-violet-600 dark:text-violet-400"
                label="New posts about your tickers"
                sub="Open the Feed to weigh in"
                value={data.portfolio_buzz.count}
              />
              <div className="mt-2 space-y-1.5">
                {data.portfolio_buzz.preview.map((p) => (
                  <div key={p.post_id} className="rounded-lg bg-muted/30 px-3 py-2 text-xs">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-mono font-bold text-violet-700 dark:text-violet-300">{p.ticker}</span>
                      <SentimentPill label={p.sentiment_label} />
                      <span className="text-muted-foreground">· @{p.handle}</span>
                    </div>
                    <p className="font-semibold mt-0.5 line-clamp-1">{p.title}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {hasExDiv ? (
            <div>
              <SectionRow
                icon={Calendar}
                iconCls="bg-emerald-100 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400"
                label="Upcoming ex-dividend dates"
                sub="Next 14 days · tickers you hold"
                value={data.upcoming_ex_divs.length}
              />
              <ExDivList items={data.upcoming_ex_divs} />
            </div>
          ) : null}

          {hasSent ? (
            <div>
              <SectionRow
                icon={TrendingUp}
                iconCls="bg-sky-100 dark:bg-sky-950/40 text-sky-600 dark:text-sky-400"
                label="Community sentiment on your holdings"
                sub="Last 30 days · bullish / bearish %"
                value={data.sentiment_held.length}
              />
              <SentimentList items={data.sentiment_held} />
            </div>
          ) : null}

          {hasRank ? (
            <SectionRow
              icon={Trophy}
              iconCls="bg-amber-100 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400"
              label={`Your rank in ${data.my_leaderboard.theme_name}`}
              sub="Updates live until Friday 4pm ET"
              value={`#${data.my_leaderboard.rank} · ${data.my_leaderboard.score_display}`}
            />
          ) : null}

          {hasPodium ? <PodiumList podium={data.last_friday_podium} /> : null}

          {/* General community behavior disclaimer */}
          <div className="mt-3 rounded-lg border border-border bg-muted/20 p-3 text-[11px] text-muted-foreground leading-snug">
            <strong className="text-foreground">A quick reminder:</strong> DiviDrip is an
            educational community. Be respectful — no spam, no pump-and-dump,
            no financial advice masquerading as fact. Reports are weighted
            by member tier and an admin reviews every action. By engaging
            you agree to the <a href="/community/terms" className="underline text-violet-600 dark:text-violet-400" onClick={stopBubble}>Community Terms</a>.
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-card border-t border-border px-5 py-3 flex justify-end">
          <Button
            onClick={close}
            className="bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="welcome-back-ok-btn"
          >
            OK, take me to the Community
          </Button>
        </div>
      </div>
    </div>
  );
}
